import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Drawer } from "@/components/ui/drawer";
import { Pencil, Trash, Plus, Search, X, Settings } from "lucide-react";
import {
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { getApiUrl } from "@/utils/api";
import { toast } from "react-toastify";
import { DeleteModal } from "@/components/delete-modal";
import { useTranslation } from "react-i18next";
import { Pagination } from "@/components/pagination";
import { VehicleTypeForm } from "./VehicleTypesForm";
import { PageHeader } from "@/components/PageHeader";

interface VehicleType {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

interface ApiResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const VehicleTypesList = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);

  const PAGE_SIZE = 10;

  // Fetch vehicle types
  const { data, isLoading, error, refetch } = useQuery<
    ApiResponse<VehicleType>
  >({
    queryKey: [
      "vehicle-types",
      currentPage,
      isSearchActive ? searchQuery : undefined,
    ],
    queryFn: async () => {
      const offset = (currentPage - 1) * PAGE_SIZE;
      let url = `vehicle-types/?limit=${PAGE_SIZE}&offset=${offset}`;

      if (isSearchActive && searchQuery) {
        url += `&name=${encodeURIComponent(searchQuery)}`;
      }

      const response = await fetch(getApiUrl(url), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(t("vehicleTypes.error.fetch"));
      }
      return response.json();
    },
    enabled: !!token,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(getApiUrl(`vehicle-types/${id}/`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || t("vehicleTypes.error.delete"));
      }
      return id; // Return the deleted ID for use in onSuccess
    },
    onSuccess: () => {
      // Invalidate and refetch the list
      queryClient.invalidateQueries({ queryKey: ["vehicle-types"] });

      // Show success message
      toast.success(t("vehicleTypes.deleteSuccess"));

      // Only close the modal after successful deletion
      setItemToDelete(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || t("vehicleTypes.error.delete"));
    },
  });

  const handleAdd = () => {
    setEditingId(null);
    setIsDrawerOpen(true);
  };

  const handleEdit = (id: number) => {
    setEditingId(id);
    setIsDrawerOpen(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setIsSearchActive(true);
      setCurrentPage(1);
    }
  };

  const handleResetSearch = () => {
    setSearchQuery("");
    setIsSearchActive(false);
    setCurrentPage(1);
  };

  // Reset search when search query becomes empty
  useEffect(() => {
    if (!searchQuery.trim()) {
      setIsSearchActive(false);
    }
  }, [searchQuery]);

  const handleDelete = () => {
    if (itemToDelete) {
      deleteMutation.mutate(itemToDelete);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500">
        {t("fuelTypes.error.fetch")}: {error.message}
      </div>
    );
  }

  return (
    // <div className="space-y-4">
    //   <div className="flex justify-between items-center">
    //     <div>
    //       <h1 className="text-2xl font-bold">{t("vehicleTypes.title")}</h1>
    //       <p className="text-muted-foreground">
    //         {t("vehicleTypes.description")}
    //       </p>
    //     </div>
    //     <Button variant="default" onClick={handleAdd}>
    //       <Plus className="mr-2 h-4 w-4" />
    //       {t("vehicleTypes.add")}
    //     </Button>
    //   </div>

    <div className="flex flex-col h-full pb-8">
      <PageHeader
        icon={<Settings className="w-12 h-12 text-primary-foreground" />}
        actionTitle="vehicleTypes.add"
        title="vehicleTypes.title"
        description="vehicleTypes.description"
        handleNew={handleAdd}
      />

      <div className="flex items-center space-x-2 p-4" />

      <div className="px-4">
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("vehicleTypes.table.id")}</TableHead>
                <TableHead>{t("vehicleTypes.table.name")}</TableHead>
                <TableHead className="text-right">
                  {t("vehicleTypes.table.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.results?.length ? (
                data.results.map((vehicleType) => (
                  <TableRow key={vehicleType.id}>
                    <TableCell variant="compact" className="font-medium">
                      {vehicleType.id}
                    </TableCell>
                    <TableCell variant="compact" className="font-medium">
                      {vehicleType.name}
                    </TableCell>
                    <TableCell
                      variant="compact"
                      className="text-right space-x-2"
                    >
                      <div className="flex justify-center items-center">
                        <div
                          role="button"
                          className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
                          onClick={() => handleEdit(vehicleType.id)}
                        >
                          <Pencil
                            size={30}
                            fill="#000000"
                            stroke="#ffffff"
                            strokeWidth={1}
                          />
                        </div>
                        <div
                          role="button"
                          className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
                          onClick={() => setItemToDelete(vehicleType.id)}
                        >
                          <Trash
                            size={30}
                            fill="#ff0000"
                            stroke="#ffffff"
                            strokeWidth={2}
                          />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={2}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {t("fuelTypes.noData")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {data && data.count > PAGE_SIZE && (
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(data.count / PAGE_SIZE)}
            hasNextPage={!!data.next}
            hasPreviousPage={!!data.previous}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <Drawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        direction="right"
      >
        <DrawerContent className="h-full w-[500px] ml-auto">
          <div className="max-h-[80vh] overflow-y-auto p-6">
            <DrawerHeader>
              <DrawerTitle>
                {editingId ? t("fuelTypes.editTitle") : t("fuelTypes.addTitle")}
              </DrawerTitle>
              <DrawerDescription>
                {editingId
                  ? t("fuelTypes.editDescription")
                  : t("fuelTypes.addDescription")}
              </DrawerDescription>
            </DrawerHeader>
            <div className="mt-4">
              <VehicleTypeForm
                id={editingId}
                onSuccess={() => {
                  setIsDrawerOpen(false);
                  queryClient.invalidateQueries({ queryKey: ["fuel-types"] });
                }}
                onCancel={() => setIsDrawerOpen(false)}
              />
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <DeleteModal
        open={!!itemToDelete}
        onOpenChange={(open) => !open && setItemToDelete(null)}
        onDelete={handleDelete}
        title={t("vehicleTypes.deleteTitle")}
        entity={t("vehicleTypes.title")}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
};
